<!-- 游戏浮层：点击游戏卡片进入的第三方游戏播放页。
     打开即调 enter 取 launch_url / transfer_amount / transfer_currency；提示卡「额度转换成功」→「复制成功」后点开始游戏直接 iframe 进入；
     右上角悬浮菜单可拖动，展开 退出/订金/转移/客服 -->
<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="gp">
        <div
          v-if="open"
          ref="containerEl"
          class="gp"
          role="dialog"
          aria-modal="true"
          :aria-label="venueName"
        >
          <!-- 游戏舞台：点开始后用已拿到的 launch_url 开 iframe，否则封面模糊底 + 加载态 -->
          <div class="gp-stage">
            <iframe
              v-if="frameSrc"
              :src="frameSrc"
              class="gp-frame"
              title="game"
              allow="fullscreen; autoplay; payment; clipboard-write"
              allowfullscreen
              referrerpolicy="no-referrer-when-downgrade"
            />
            <template v-else>
              <img
                v-if="game?.imageUrl"
                :src="game.imageUrl"
                class="gp-stage-bg"
                alt=""
                aria-hidden="true"
                crossorigin="anonymous"
              />
              <div class="gp-stage-mask" />
              <div class="gp-loading">
                <span class="gp-spinner" aria-hidden="true" />
                <span class="gp-loading-text">{{ t('game.play.loading') }}</span>
              </div>
              <div v-if="game" class="gp-stage-name">{{ game.title }}</div>
            </template>
          </div>

          <!-- 右上角悬浮菜单（可拖动，避免挡游戏操作区） -->
          <div
            class="gp-menu"
            :class="{ 'is-open': menuOpen, 'is-dragging': menuDragging, 'is-flip': menuFlip }"
            :style="menuStyle"
          >
            <button
              type="button"
              class="gp-menu-trigger"
              :aria-expanded="menuOpen"
              :aria-label="menuOpen ? t('game.play.menu.collapse') : t('game.play.menu.expand')"
              @pointerdown="onMenuPointerDown"
            >
              <svg v-if="menuOpen" viewBox="0 0 24 24" class="gp-menu-ic" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  fill="none"
                />
              </svg>
              <svg v-else viewBox="0 0 24 24" class="gp-menu-ic" aria-hidden="true">
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  fill="none"
                />
              </svg>
            </button>

            <Transition name="gp-actions">
              <div v-if="menuOpen" class="gp-actions">
                <button type="button" class="gp-action" @click="onExit">
                  <svg viewBox="0 0 24 24" class="gp-action-ic" aria-hidden="true">
                    <path
                      d="M12 3v9"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      fill="none"
                    />
                    <path
                      d="M7.2 6.4a7 7 0 1 0 9.6 0"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      fill="none"
                    />
                  </svg>
                  <span class="gp-action-label">{{ t('game.play.menu.exit') }}</span>
                </button>
                <button type="button" class="gp-action" @click="onDeposit">
                  <svg viewBox="0 0 24 24" class="gp-action-ic" aria-hidden="true">
                    <path
                      d="M12 2.5v19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      fill="none"
                    />
                    <path
                      d="M16.5 6.8c-.9-1.4-2.6-2.3-4.5-2.3-2.5 0-4.3 1.4-4.3 3.4S9.5 11 12 11.4s4.3 1.4 4.3 3.5-1.8 3.4-4.3 3.4c-1.9 0-3.6-.9-4.5-2.3"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      fill="none"
                    />
                  </svg>
                  <span class="gp-action-label">{{ t('game.play.menu.deposit') }}</span>
                </button>
                <button type="button" class="gp-action" @click="onTransfer">
                  <AppIcon name="me-transfer" class="gp-action-ic" />
                  <span class="gp-action-label">{{ t('game.play.menu.transfer') }}</span>
                </button>
                <button v-if="supportEnabled" type="button" class="gp-action" @click="onSupport">
                  <AppIcon name="headset" class="gp-action-ic" />
                  <span class="gp-action-label">{{ t('game.play.menu.support') }}</span>
                </button>
              </div>
            </Transition>
          </div>

          <!-- 中间提示卡：额度转换成功 → 复制成功 -->
          <Transition name="gp-prompt">
            <div v-if="promptVisible" class="gp-prompt" @click.self="dismissPrompt">
              <div class="gp-card">
                <h3 class="gp-card-title">
                  {{
                    stage === 'transfer' ? t('game.play.transferTitle') : t('game.play.copyTitle')
                  }}
                </h3>
                <p class="gp-card-label">{{ t('game.play.balanceLabel', { venue: venueName }) }}</p>
                <p class="gp-card-amount">{{ amountText }}</p>
                <!-- USDT 站：场馆内 ¥ 与 USDT 1:1 计价提示 -->
                <p v-if="showMappedRate" class="gp-card-rate">
                  {{ t('game.play.mappedRatePrefix') }}
                  <span class="gp-card-rate-eq">{{
                    t('game.play.mappedRateEq', { code: 'USDT' })
                  }}</span>
                  {{ t('game.play.mappedRateSuffix') }}
                </p>
                <p class="gp-card-desc">
                  {{ stage === 'transfer' ? t('game.play.transferDesc') : t('game.play.copyDesc') }}
                </p>
                <button type="button" class="gp-card-cta" :disabled="!canStart" @click="startGame">
                  {{ entering ? t('game.play.loading') : `${t('game.play.start')} →` }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import { enterGame } from '~/api/games'
import { recoverAllVenues } from '~/api/wallet'
import { parseMoney, formatMoneyPlain } from '~/utils/transfer/money'

const { t } = useI18n()
const route = useRoute()
const userStore = useUserStore()
const { open, game, closeGame, exitGame } = useGamePlayNav()
const { openTransfer } = useTransferNav()
const { openWalletPushed } = useWalletNav()
const { refreshWallet } = useWallet()

const { lockWhile } = useBodyScrollLock()
lockWhile(open)

/** 提示卡两态：先额度转换成功，自动转复制成功 */
const stage = ref<'transfer' | 'copy'>('transfer')
const promptVisible = ref(true)
/** enter 返回的启动地址，点开始游戏时直接用于 iframe */
const launchUrl = ref('')
/** 真正挂载到 iframe 的地址（点开始后才赋值，避免提示卡阶段就开始加载） */
const frameSrc = ref('')
const entering = ref(false)
/** enter 返回的转入金额 */
const transferAmount = ref<string | null>(null)
/** enter 返回的转入币种 */
const transferCurrency = ref<string | null>(null)

const containerEl = ref<HTMLElement | null>(null)
const {
  dragging: menuDragging,
  flip: menuFlip,
  open: menuOpen,
  style: menuStyle,
  reset: resetMenu,
  onPointerDown: onMenuPointerDown,
} = useDraggableFab({ containerEl })

let stageTimer: ReturnType<typeof setTimeout> | null = null
let enterSeq = 0

const venueName = computed(() => game.value?.provider || game.value?.title || 'AG真人')

/** 展示币种：优先 enter 返回，否则回退站点基础币种 */
const displayCurrency = computed(() =>
  (transferCurrency.value || userStore.baseCurrency || '').toUpperCase(),
)

/** 金额按转入币种格式化；USDT 展示 xx USDT，其余按 ¥ / 元 */
const isUsdtAmount = computed(() => displayCurrency.value === 'USDT')

/** USDT 站统一展示 1 ¥ = 1 USDT 计价提示 */
const showMappedRate = computed(() => userStore.baseCurrency === 'USDT')

const amountValue = computed<number | null>(() => {
  const raw = transferAmount.value
  if (raw == null || raw === '') return null
  return parseMoney(raw)
})

/**
 * CNY / 映射场馆：额度转换态 ¥xx · 复制成功态 xx元
 * USDT：xx USDT
 * 金额 / 币种来自 enter.transfer_amount / transfer_currency
 */
const amountText = computed(() => {
  if (amountValue.value == null) return t('me.tfUnknown')
  const n = formatMoneyPlain(amountValue.value)
  if (isUsdtAmount.value) return `${n} USDT`
  return stage.value === 'transfer' ? `¥${n}` : `${n}元`
})

const canStart = computed(() => !entering.value && Boolean(launchUrl.value))

/** 静默写入剪贴板（提示卡已展示「复制成功」，不再弹 toast） */
const copySilent = async (text: string): Promise<boolean> => {
  if (!import.meta.client || !text) return false

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', 'readonly')
  textarea.style.cssText =
    'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;box-shadow:none;background:transparent;font-size:12pt;'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, text.length)
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(textarea)
  if (ok) return true

  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** 拿到 launch_url 后写入剪贴板；「复制成功」文案依赖这次写入 */
const copyLaunchUrlIfReady = async () => {
  const url = launchUrl.value
  if (!url) return false
  return copySilent(url)
}

const clearStageTimer = () => {
  if (stageTimer) {
    clearTimeout(stageTimer)
    stageTimer = null
  }
}

const resetPlayState = () => {
  stage.value = 'transfer'
  promptVisible.value = true
  resetMenu()
  launchUrl.value = ''
  frameSrc.value = ''
  entering.value = false
  transferAmount.value = null
  transferCurrency.value = null
}

/** 打开浮层即调 enter，取 launch_url 与转入金额 */
const requestEnter = async () => {
  const id = game.value?.id
  if (!id) return

  const seq = ++enterSeq
  entering.value = true
  try {
    const data = await enterGame(id, {
      currency: userStore.baseCurrency,
    })
    if (seq !== enterSeq) return
    launchUrl.value = data?.launch_url?.trim() || ''
    transferAmount.value = data?.transfer_amount?.trim() || null
    transferCurrency.value = data?.transfer_currency?.trim() || null
    // 有启动地址则写入剪贴板，供「复制成功」态粘贴
    if (launchUrl.value) await copyLaunchUrlIfReady()
  } catch {
    if (seq !== enterSeq) return
    launchUrl.value = ''
    transferAmount.value = null
    transferCurrency.value = null
  } finally {
    if (seq === enterSeq) entering.value = false
  }
}

/** 每次打开：重置提示卡与菜单位置（回右上角），并立即调 enter */
watch(open, v => {
  clearStageTimer()
  enterSeq += 1
  if (v) {
    resetPlayState()
    void requestEnter()
    stageTimer = setTimeout(() => {
      // 若 enter 已返回则再补一次复制（接口晚到时上面已写过）
      void copyLaunchUrlIfReady().finally(() => {
        stage.value = 'copy'
      })
    }, 1200)
  } else {
    launchUrl.value = ''
    frameSrc.value = ''
    entering.value = false
    transferAmount.value = null
    transferCurrency.value = null
    resetMenu()
  }
})

onBeforeUnmount(clearStageTimer)

const dismissPrompt = () => {
  if (!canStart.value) return
  startGame()
}

/** 点开始游戏：直接用已拿到的 launch_url 进 iframe，不再请求；再补一次复制兜底 */
const startGame = () => {
  if (!canStart.value) return
  void copyLaunchUrlIfReady()
  promptVisible.value = false
  frameSrc.value = launchUrl.value
}

/** 退出后收回场馆余额并刷新中心钱包，避免展示额停留在进游戏前 */
const recoverWalletAfterExit = async () => {
  try {
    const res = await recoverAllVenues()
    userStore.setAvailableDisplay(res.central_available_display)
  } catch {
    // 找回失败不阻断退出；仍尝试拉最新 balances
  }
  await refreshWallet().catch(() => {})
}

const onExit = () => {
  menuOpen.value = false
  exitGame()
  void recoverWalletAfterExit()
}

const onDeposit = () => {
  menuOpen.value = false
  const from = route.fullPath
  closeGame()
  openWalletPushed(from)
}

const onTransfer = () => {
  menuOpen.value = false
  openTransfer()
}

// 客服入口：后台「站点设置 → 客服配置」没开通时菜单里不显示这一项
const { supportEnabled, openSupport } = useSupport()

const onSupport = () => {
  menuOpen.value = false
  openSupport()
}
</script>

<style scoped lang="scss">
.gp {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  /* 低于登录/额度转换抽屉(55)，「转移」可盖在游戏浮层之上；高于底导(50) */
  z-index: 52;
  max-width: 480px;
  margin: 0 auto;
  background: #05060f;
  overflow: hidden;
}

/* ---------- 游戏舞台（加载态 / iframe） ---------- */
.gp-stage {
  position: absolute;
  inset: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gp-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: #05060f;
}

.gp-stage-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(18px) brightness(0.5) saturate(1.1);
  transform: scale(1.15);
}

.gp-stage-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(120% 90% at 50% 40%, rgba(8, 8, 24, 0.35), rgba(4, 6, 16, 0.9));
}

.gp-loading {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.gp-loading-text {
  font-size: 13px;
  letter-spacing: 0.05em;
}

.gp-spinner {
  width: 26px;
  height: 26px;
  border: 3px solid rgba(255, 255, 255, 0.18);
  border-top-color: var(--primary, #8b5cff);
  border-radius: 50%;
  animation: gp-spin 0.8s linear infinite;
}

.gp-stage-name {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(28px + env(safe-area-inset-bottom, 0px));
  text-align: center;
  font-size: 30px;
  font-weight: 900;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.14);
  pointer-events: none;
}

/* ---------- 右上角悬浮菜单（可拖动） ---------- */
.gp-menu {
  position: absolute;
  top: calc(12px + env(safe-area-inset-top, 0px));
  right: 12px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  width: 58px;
  touch-action: none;
  user-select: none;

  &.is-flip {
    flex-direction: column-reverse;
  }

  &.is-dragging {
    opacity: 0.85;
  }
}

.gp-menu-trigger {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  background: rgba(10, 10, 20, 0.62);
  color: #fff;
  backdrop-filter: blur(6px);
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
    transform: scale(0.92);
  }
}

.gp-menu.is-dragging .gp-menu-trigger {
  cursor: grabbing;
  transform: scale(1.05);
}

.gp-menu-ic {
  width: 20px;
  height: 20px;
}

.gp-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gp-action {
  width: 58px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 9px 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  background: rgba(10, 10, 20, 0.62);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(6px);

  &:active {
    transform: scale(0.94);
  }
}

.gp-action-ic {
  width: 20px;
  height: 20px;
  font-size: 20px;
  color: #fff;
}

.gp-action-label {
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: #fff;
  /* Chrome 最小字号约 12px：12 * 0.833 ≈ 10 */
  transform: scale(0.833);
  transform-origin: center center;
}

/* ---------- 中间提示卡 ---------- */
.gp-prompt {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(4, 6, 16, 0.28);
}

.gp-card {
  width: 100%;
  max-width: 300px;
  padding: 24px 20px 20px;
  border-radius: 18px;
  border: 1px solid rgba(139, 92, 255, 0.28);
  background: linear-gradient(160deg, rgba(38, 30, 74, 0.96), rgba(24, 20, 48, 0.96));
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
  text-align: center;
}

.gp-card-title {
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
}

.gp-card-label {
  margin: 0 0 6px;
  font-size: 12px;
  color: var(--gold-mid, #ffb454);
}

.gp-card-amount {
  margin: 0 0 14px;
  max-width: 100%;
  font-size: 28px;
  font-weight: 900;
  color: var(--gold-hi, #ffd76b);
  letter-spacing: 0.01em;
  overflow-wrap: anywhere;
}

.gp-card-rate {
  margin: -4px 0 14px;
  padding: 8px 10px;
  border: 1px solid rgba(139, 92, 255, 0.35);
  border-radius: 8px;
  background: rgba(139, 92, 255, 0.15);
  font-size: 12px;
  line-height: 1.5;
  color: #8b5cff;
  overflow-wrap: anywhere;
}

.gp-card-rate-eq {
  color: #fff;
  font-weight: 700;
  white-space: nowrap;
}

.gp-card-desc {
  margin: 0 0 18px;
  font-size: 12px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.55);
}

.gp-card-cta {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b5cff, #6a6cff);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  box-shadow: 0 8px 20px rgba(139, 92, 255, 0.42);

  &:active:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

@keyframes gp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ---------- 过渡 ---------- */
.gp-enter-active,
.gp-leave-active {
  transition: opacity 0.26s ease;
}

.gp-enter-from,
.gp-leave-to {
  opacity: 0;
}

.gp-prompt-enter-active,
.gp-prompt-leave-active {
  transition: opacity 0.2s ease;
}

.gp-prompt-enter-from,
.gp-prompt-leave-to {
  opacity: 0;
}

.gp-actions-enter-active,
.gp-actions-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.gp-actions-enter-from,
.gp-actions-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
